import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';

import {
  creditCardValidationSchema,
  type CreditCardValidationValues,
} from 'app/schemas/creditCardValidation';

export type CheckoutSavedCard = {
  id: number;
  hash?: string;
  brand: string;
  last_digits: string;
  expiration_date: string;
  in_use?: boolean;
  is_primary?: boolean;
};

type PaymentMethodRelation = {
  id: string;
  svg?: string;
};

type CheckoutPaymentMethodReduxState = {
  app: {
    cards: CheckoutSavedCard[];
    payment_methods_relation: PaymentMethodRelation[];
    is_loading_cards?: boolean;
  };
};

export type CheckoutPaymentMethodMode = 'loading' | 'saved' | 'new';

export type CheckoutSavedCardDisplay = {
  id: number;
  brandLabel: string;
  brandIconUrl: string | null;
  lastDigits: string;
  expirationDate: string;
  isPrimary: boolean;
};

const NEW_CARD_INITIAL_VALUES: CreditCardValidationValues = {
  cardholder_name: '',
  card_number: '',
  card_expiration: '',
  card_cvv: '',
  card_selected: null,
};

const isPrimaryCard = (card: CheckoutSavedCard): boolean =>
  Boolean(card.in_use || card.is_primary);

const capitalizeBrand = (brand: string): string => {
  if (!brand) {
    return '';
  }

  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
};

export function useCheckoutPaymentMethod() {
  const dispatch = useDispatch();

  const cards = useSelector(
    (state: CheckoutPaymentMethodReduxState) => state.app.cards,
  );
  const paymentMethods = useSelector(
    (state: CheckoutPaymentMethodReduxState) =>
      state.app.payment_methods_relation,
  );
  const isLoadingCards = useSelector((state: CheckoutPaymentMethodReduxState) =>
    Boolean(state.app.is_loading_cards),
  );

  useEffect(() => {
    if (cards.length === 0 && !isLoadingCards) {
      dispatch({ type: 'LOAD_CARDS' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only; re-running on cards would flash loading
  }, [dispatch]);

  const [selectedMode, setSelectedMode] = useState<'saved' | 'new'>('saved');
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);

  const primaryCard = useMemo(
    () => cards.find(isPrimaryCard) ?? cards[0] ?? null,
    [cards],
  );

  const savedCards: CheckoutSavedCardDisplay[] = useMemo(
    () =>
      cards.map((card) => {
        const brandKey = card.brand?.toLowerCase() ?? '';
        const brandIconUrl =
          paymentMethods.find((method) => method.id.toLowerCase() === brandKey)
            ?.svg ?? null;

        return {
          id: card.id,
          brandLabel: capitalizeBrand(card.brand),
          brandIconUrl,
          lastDigits: card.last_digits,
          expirationDate: card.expiration_date,
          isPrimary: isPrimaryCard(card),
        };
      }),
    [cards, paymentMethods],
  );

  const hasSavedCard = !isLoadingCards && cards.length > 0;

  const selectedSavedCardId =
    selectedCardId != null && cards.some((card) => card.id === selectedCardId)
      ? selectedCardId
      : (primaryCard?.id ?? null);

  const selectedRawCard = useMemo(
    () => cards.find((card) => card.id === selectedSavedCardId) ?? null,
    [cards, selectedSavedCardId],
  );

  const mode: CheckoutPaymentMethodMode = isLoadingCards
    ? 'loading'
    : hasSavedCard
      ? selectedMode
      : 'new';

  const formik = useFormik<CreditCardValidationValues>({
    initialValues: NEW_CARD_INITIAL_VALUES,
    validationSchema: creditCardValidationSchema,
    onSubmit: () => {},
  });

  const isPaymentValid = useMemo(() => {
    if (mode === 'loading') {
      return false;
    }

    if (mode === 'saved') {
      return true;
    }

    return creditCardValidationSchema.isValidSync(formik.values);
  }, [mode, formik.values]);

  const selectSavedCard = (cardId?: number) => {
    setSelectedMode('saved');
    if (cardId != null) {
      setSelectedCardId(cardId);
    }
  };

  return {
    mode,
    hasSavedCard,
    savedCards,
    selectedSavedCardId,
    rawSavedCard: selectedRawCard,
    selectSavedCard,
    selectNewCard: () => setSelectedMode('new'),
    formik,
    isPaymentValid,
  };
}
